import { useEffect, useRef, useState } from "react";
import { BackwardButton, ForwardButton } from "./blogCarouselButtons";
import { Articles } from "../../data/models/article";
import RelatedArticlesItem from "./relatedArticlesItem";

interface Props {
  allArticles: Articles[];
  currentArticle: Articles;
}

export const RelatedArticlesCarousel = ({
  allArticles,
  currentArticle,
}: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const autoScrollInterval = useRef<NodeJS.Timeout>();

  // Filter articles that share at least one tag with the current article
  const relatedArticles = allArticles.filter((article) => {
    // Skip the current article
    if (article.id === currentArticle.id) return false;

    // Get current article's tag IDs
    const currentTags = currentArticle.tags?.map((tag) => tag.id) || [];
    // Get potential related article's tag IDs
    const articleTags = article.tags?.map((tag) => tag.id) || [];

    // If either article has no tags, don't show as related
    if (currentTags.length === 0 || articleTags.length === 0) return false;

    // Check if they share any tags
    return articleTags.some((tagId) => currentTags.includes(tagId));
  });

  // Auto-scroll functionality for mobile
  useEffect(() => {
    const startAutoScroll = () => {
      autoScrollInterval.current = setInterval(() => {
        setCurrentIndex((prevIndex) =>
          prevIndex + 1 >= relatedArticles.length ? 0 : prevIndex + 1,
        );
      }, 5000);
    };

    const stopAutoScroll = () => {
      if (autoScrollInterval.current) {
        clearInterval(autoScrollInterval.current);
      }
    };

    if (window.innerWidth < 1024) {
      startAutoScroll();
    }

    return () => {
      stopAutoScroll();
    };
  }, [relatedArticles.length]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        if (autoScrollInterval.current) {
          clearInterval(autoScrollInterval.current);
        }
      } else {
        if (!autoScrollInterval.current) {
          autoScrollInterval.current = setInterval(() => {
            setCurrentIndex((prevIndex) =>
              prevIndex + 1 >= relatedArticles.length ? 0 : prevIndex + 1,
            );
          }, 5000);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [relatedArticles.length]);

  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex + 1 >= relatedArticles.length ? 0 : prevIndex + 1,
    );
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex - 1 < 0 ? relatedArticles.length - 1 : prevIndex - 1,
    );
  };

  if (relatedArticles.length === 0) return null;

  return (
    <div className="flex flex-col">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-[8px]">
          <p className="font-inter text-[30px] font-medium leading-[125%] tracking-[-0.02em] text-[#131313] lg:text-[42px]">
            Related Articles
          </p>
        </div>
        <div className="mt-[24px] hidden flex-row gap-5 lg:flex">
          <BackwardButton
            onClick={handlePrev}
            className="bg-[#FFF3ED]"
            iconSrc="/assets/icons/Arrow_Back.svg"
          />
          <ForwardButton onClick={handleNext} className="bg-[#022928]" />
        </div>
      </div>

      {/* Desktop View */}
      <div className="hidden lg:block">
        <div className="relative mt-10 overflow-hidden">
          <div
            className="flex gap-x-5 transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * 33.33}%)` }}
          >
            {relatedArticles.map((article, index) => (
              <RelatedArticlesItem key={article.id} article={article} />
            ))}
          </div>
        </div>
      </div>

      {/* Mobile View */}
      <div className="block lg:hidden">
        <div className="relative mt-[42px] overflow-x-auto">
          <div className="flex gap-x-5 transition-transform duration-500 ease-in-out">
            {relatedArticles.map((article) => (
              <div className="min-w-[298px]" key={article.id}>
                <RelatedArticlesItem article={article} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
